import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SEO from "../../components/base/SEO";

const services = [
  {
    icon: "ri-fire-line",
    title: "Furnace Installation & Replacement",
    desc: "New high-efficiency gas, oil, or propane furnace installation — including full system sizing, load calculations, and rebate-eligible equipment.",
  },
  {
    icon: "ri-tools-line",
    title: "Furnace Repair",
    desc: "Fast diagnostics and same-day repair on all makes and models. Our technicians carry the most common parts on every truck.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Annual Heating Tune-Ups",
    desc: "Pre-season maintenance to maximize efficiency, extend equipment life, and catch issues before they become emergencies.",
  },
  {
    icon: "ri-water-flash-line",
    title: "Boiler Service & Installation",
    desc: "Expert service for hot-water and steam boiler systems — hydronic heat, baseboard, and radiant floor heating.",
  },
  {
    icon: "ri-temp-hot-line",
    title: "Heat Pump Heating",
    desc: "Cold-climate ductless and ducted heat pumps that provide efficient, electric heating down to -13°F — no furnace needed.",
  },
  {
    icon: "ri-home-gear-line",
    title: "Ductwork & Zoning",
    desc: "Duct sealing, replacement, and zoning systems so every room heats evenly and efficiently without waste.",
  },
];

const systemTypes = [
  {
    name: "Gas & Propane Furnaces",
    tagline: "The most common heating system in CT",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/2b150c5ac8021fbb90ab5482e4328257.jpeg",
    desc: "Modern gas furnaces achieve up to 98% AFUE efficiency, cutting fuel costs dramatically over older equipment. We install, replace, and service all major brands with proper sizing for your home.",
    features: ["Up to 98% AFUE efficiency", "Variable-speed blower motors", "Qualifies for CT rebates", "Single & two-stage options"],
    highlight: false,
  },
  {
    name: "Water Heaters",
    tagline: "Expert service for oil-heated homes",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/629718d984265280e7c81e10e164ce8b.jpeg",
    desc: "Many Connecticut homes still rely on oil heating. We service and replace oil furnaces and boilers, and can convert systems to natural gas or heat pump where available.",
    features: ["Oil furnace & boiler service", "Oil-to-gas conversion available", "All major brands", "Emergency service available"],
    highlight: false,
  },
  {
    name: "Cold-Climate Heat Pumps",
    tagline: "Electric heating with serious efficiency",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/df89ed67cf5acaffdb6826e40b2cd1e3.jpeg",
    desc: "Mitsubishi Hyper Heat and similar cold-climate systems heat your home efficiently down to -13°F — qualifying for federal tax credits and CT utility rebates. A smart alternative or supplement to fossil fuels.",
    features: ["Works to -13°F", "Up to 30% federal tax credit", "Pairs with existing system", "Lowest operating cost"],
    highlight: true,
  },
  {
    name: "Boiler & Hydronic Systems",
    tagline: "Radiant, baseboard & zone heating",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/b8242bbab5a4b52805cd3ddb75a17d3e.jpeg",
    desc: "Hot-water boilers deliver radiant warmth through baseboard radiators and in-floor systems. We install, service, and upgrade boiler systems throughout Connecticut with full zone control.",
    features: ["Radiant & baseboard systems", "Zone control thermostats", "High-efficiency condensing boilers", "Annual service plans available"],
    highlight: false,
  },
];

const brands = [
  { name: "Carrier", note: "Factory Authorized Dealer — WeatherMaker & Infinity series" },
  { name: "Mitsubishi", note: "Diamond Dealer — Hyper Heat cold-climate systems" },
  { name: "Crown", note: "Gold Partner — high-efficiency ducted & ductless" },
  { name: "Bosch", note: "Certified — IDS Premium & high-AFUE furnaces" },
  { name: "Lennox", note: "Authorized — oil & gas boilers, hydronic systems" },
];

const faqs = [
  {
    q: "When should I replace my furnace instead of repairing it?",
    a: "A good rule of thumb: if your furnace is over 15 years old and the repair cost exceeds 50% of replacement cost, replacement usually makes more financial sense. We&apos;ll give you an honest assessment — not just the option that earns us more money.",
  },
  {
    q: "How much does a new furnace installation cost in Connecticut?",
    a: "Most gas furnace replacements run $3,000–$6,500 installed, depending on equipment size, efficiency rating, and installation complexity. We provide free written estimates with no obligation.",
  },
  {
    q: "What AFUE rating should I look for in a new furnace?",
    a: "For Connecticut winters, we recommend 96% AFUE or higher. The efficiency premium pays for itself in 3–5 years through fuel savings, and these units often qualify for rebates from Eversource and UI.",
  },
  {
    q: "Do you service all brands of furnaces and boilers?",
    a: "Yes — our technicians are trained to service all major brands including Carrier, Lennox, Trane, Rheem, Bryant, Weil-McLain, Burnham, and more. We carry common parts on every truck to minimize return visits.",
  },
  {
    q: "Are there rebates available for new heating equipment in CT?",
    a: "Yes. Eversource and UI offer rebates on qualifying high-efficiency furnaces, boilers, and heat pumps. Federal tax credits also apply to heat pump systems (up to $2,000). We help you identify and apply for every incentive available.",
  },
  {
    q: "How often should I have my heating system serviced?",
    a: "Annual tune-ups are recommended — ideally in early fall before heating season. Regular maintenance extends equipment life, maintains efficiency, and keeps your warranty valid. Our service contract plans include annual heating tune-ups at a discounted rate.",
  },
];

const siteUrl: string = (import.meta.env.VITE_SITE_URL as string) ?? "";

const heatingJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Heating Repair & Furnace Installation Connecticut | Ultimate Heating & Cooling",
    description:
      "Expert furnace installation, heating repair, and boiler service in Connecticut. Gas, oil, propane & heat pump heating systems. Free estimates — call (959) 241-8524.",
    url: `${siteUrl}/heating`,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Heating", item: `${siteUrl}/heating` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

export default function HeatingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Heating Repair & Furnace Installation Connecticut | Ultimate Heating & Cooling"
        description="Expert furnace installation, heating repair, and boiler service in Connecticut. Gas, oil, propane & heat pump heating systems. Free estimates available."
        keywords="furnace installation Connecticut, heating repair CT, boiler service Somers CT, HVAC heating contractor Connecticut"
        canonical="/heating"
        jsonLd={heatingJsonLd}
      />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/212d55906e81ac9f5c6349c8b18f2076.jpeg"
          alt="Heating services Connecticut"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2447]/92 via-[#0B2447]/70 to-[#0B2447]/20"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <span className="inline-block bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 rounded-full mb-5">
              HEATING SERVICES
            </span>
            <h1
              className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5 max-w-2xl"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Connecticut Heating<br />Experts You Can Trust
            </h1>
            <p className="text-white/80 text-base max-w-xl leading-relaxed mb-8">
              From furnace installation to emergency heating repair, Ultimate Heating &amp; Cooling keeps your Connecticut home warm all winter — with fast response and honest pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap w-fit"
              >
                GET A FREE ESTIMATE
              </Link>
              <a
                href="tel:9592418524"
                className="border border-white/40 text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap w-fit flex items-center gap-2"
              >
                <i className="ri-phone-line"></i>
                (959) 241-8524
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES GRID ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHAT WE DO</span>
            <h2
              className="text-2xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Full-Service Heating for Connecticut Homes
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto mt-3 leading-relaxed">
              Whether you need emergency repair, a new system, or a tune-up before winter — we handle every aspect of residential heating.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => (
              <div key={s.title} className="bg-[#F0F4FA] rounded-xl p-6 border border-gray-100">
                <div className="w-11 h-11 flex items-center justify-center bg-[#C8102E]/10 rounded-xl mb-4">
                  <i className={`${s.icon} text-xl text-[#C8102E]`}></i>
                </div>
                <h3 className="font-bold text-[#0B2447] text-sm mb-2">{s.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SYSTEM TYPES ── */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">SYSTEMS WE INSTALL</span>
            <h2
              className="text-3xl font-extrabold text-[#0B2447] mt-2 mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              The Right Heating System for Your Home
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto leading-relaxed">
              We&apos;ll help you choose the best heating solution based on your fuel type, home size, efficiency goals, and budget.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {systemTypes.map((sys) => (
              <div
                key={sys.name}
                className={`rounded-2xl overflow-hidden border-2 bg-white ${sys.highlight ? "border-[#C8102E]" : "border-gray-100"}`}
              >
                {sys.highlight && (
                  <div className="bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.18em] text-center py-2">
                    MOST ENERGY EFFICIENT
                  </div>
                )}
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={sys.image}
                    alt={sys.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[#C8102E] text-[10px] font-bold tracking-[0.15em] uppercase">{sys.tagline}</span>
                  <h3
                    className="text-lg font-extrabold text-[#0B2447] mt-1 mb-3"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {sys.name}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{sys.desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {sys.features.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 bg-[#C8102E]/10 rounded-full">
                          <i className="ri-check-line text-[#C8102E] text-[10px]"></i>
                        </span>
                        <span className="text-xs text-gray-600 leading-snug">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative w-full h-[420px] rounded-2xl overflow-hidden">
              <img
                src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/67d6fcd666fcb93f78a08da027d2355b.jpeg"
                alt="Ultimate Heating & Cooling technician"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-4 left-4 bg-white rounded-xl px-4 py-3">
                <p className="text-[#0B2447] text-xs font-bold">Factory-Trained Technicians</p>
                <p className="text-gray-500 text-[11px] mt-0.5">Certified &amp; Licensed in CT</p>
              </div>
            </div>
            <div>
              <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHY CHOOSE ULTIMATE</span>
              <h2
                className="text-3xl font-extrabold text-[#0B2447] mt-3 mb-5 leading-tight"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Heating Specialists<br />You Can Count On
              </h2>
              <div className="space-y-4">
                {[
                  {
                    icon: "ri-verified-badge-line",
                    title: "Licensed & Factory-Certified",
                    desc: "Our technicians are factory-certified by Carrier, Mitsubishi, Daikin, and more — ensuring your warranty remains fully intact after every service call.",
                  },
                  {
                    icon: "ri-money-dollar-circle-line",
                    title: "Honest, Upfront Pricing",
                    desc: "We provide detailed written estimates before any work begins. No hidden fees, no surprise invoices — just clear pricing and quality work.",
                  },
                  {
                    icon: "ri-map-pin-line",
                    title: "Connecticut-Based & Local",
                    desc: "We know CT building codes, rebate programs, and New England winters. We live and work in the same communities we serve.",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C8102E]/10 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-lg text-[#C8102E]`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0B2447] text-sm mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BRANDS ── */}
      <section className="py-16 bg-[#F0F4FA] border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">BRANDS WE INSTALL & SERVICE</span>
            <h2
              className="text-xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Authorized Dealer & Certified Installer
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {brands.map((b) => (
              <div key={b.name} className="bg-white rounded-xl px-5 py-5 text-center border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0B2447]/10 rounded-lg mx-auto mb-3">
                  <i className="ri-award-fill text-[#0B2447] text-base"></i>
                </div>
                <p className="text-[#0B2447] font-bold text-sm mb-1">{b.name}</p>
                <p className="text-gray-400 text-[11px] leading-snug">{b.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REBATE BANNER ── */}
      <section className="py-14 bg-[#0B2447]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="w-16 h-16 flex items-center justify-center bg-[#C8102E]/20 rounded-2xl flex-shrink-0">
              <i className="ri-money-dollar-circle-fill text-[#C8102E] text-3xl"></i>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase mb-2">AVAILABLE REBATES & INCENTIVES</p>
              <h3
                className="text-2xl font-extrabold text-white mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Save Up to $2,000 on Qualifying Heating Equipment
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Eversource and UI offer rebates on high-efficiency furnaces, boilers, and heat pumps. The Inflation Reduction Act adds a 30% federal tax credit on qualifying heat pump heating systems. We&apos;ll identify every incentive that applies to your project.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
              >
                GET FREE ESTIMATE
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">FAQ</span>
            <h2
              className="text-2xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Heating Questions Answered
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer group"
                >
                  <span className="text-sm font-semibold text-[#0B2447] group-hover:text-[#C8102E] transition-colors pr-4 leading-snug">
                    {faq.q}
                  </span>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${
                      openFaq === i ? "bg-[#C8102E] text-white rotate-45" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <i className="ri-add-line text-base"></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-100">
                    <p className="text-gray-500 text-sm leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20cozy%20Connecticut%20home%20interior%20winter%20evening%20living%20room%20glowing%20lights%20family%20comfortable%20residential%20interior%20design%20fireplace%20furniture%20warm%20amber%20tones%20suburban%20home&width=1400&height=400&seq=heating-cta-01&orientation=landscape"
          alt="Comfortable heated home Connecticut"
          className="w-full h-72 object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#0B2447]/85"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">READY TO GET STARTED?</span>
            <h2
              className="text-3xl font-extrabold text-white mt-3 mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Stay Warm This Winter — Call Connecticut&apos;s Heating Pros
            </h2>
            <p className="text-white/70 text-sm mb-7 max-w-md mx-auto leading-relaxed">
              Free estimates on new systems. Fast repair response. Emergency service. Let&apos;s get your heating sorted before the cold hits.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
              >
                SCHEDULE FREE ESTIMATE
              </Link>
              <a
                href="tel:9592418524"
                className="border border-white/40 text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                CALL (959) 241-8524
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
